"use client";

import { useState } from "react";

interface BlogCommentFormProps {
  blogSlug?: string;
}

const BlogCommentForm = ({ blogSlug }: BlogCommentFormProps) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<{
        type: 'success' | 'error' | null;
        message: string;
    }>({ type: null, message: '' });

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus({ type: null, message: '' });

        try {
            const response = await fetch('/api/blog-comment', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    ...formData,
                    blogSlug: blogSlug || ''
                }),
            });

            const data = await response.json();

            if (response.ok) {
                setSubmitStatus({
                    type: 'success',
                    message: 'Comment submitted successfully! Thank you for your feedback.'
                });
                setFormData({ name: '', email: '', message: '' });
            } else {
                setSubmitStatus({
                    type: 'error',
                    message: data.error || 'Something went wrong. Please try again.'
                });
            }
        } catch (error) {
            console.error('Error submitting comment:', error);
            setSubmitStatus({
                type: 'error',
                message: 'Network error. Please check your connection and try again.'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <div className="blog-contact-form row-margin-top blog-item">
                <h3>Leave a reply</h3>
                <p>
                    Your email address will not be published.
                </p>
                
                {submitStatus.type && (
                    <div className={`alert ${submitStatus.type === 'success' ? 'alert-success' : 'alert-danger'} mb-4`}>
                        {submitStatus.message}
                    </div>
                )}

                <form
                    onSubmit={handleSubmit}
                    className="desilo-cform"
                    autoComplete="off"
                >
                    <div className="row g-3">
                        <div className="col-md-6">
                            <div className="mb-2">
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    className="form-control"
                                    placeholder="Your name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    required
                                    disabled={isSubmitting}
                                />
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="mb-2">
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    className="form-control"
                                    placeholder="Enter your e-mail address"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    required
                                    disabled={isSubmitting}
                                />
                            </div>
                        </div>
                        <div className="col-12">
                            <div className="mb-2">
                                <textarea
                                    name="message"
                                    id="message"
                                    className="form-control"
                                    rows={6}
                                    placeholder="Write your comment..."
                                    value={formData.message}
                                    onChange={handleInputChange}
                                    required
                                    disabled={isSubmitting}
                                />
                            </div>
                        </div>
                        <div className="col-12 mt-4">
                            <button
                                type="submit"
                                className="desilo-link-btn btn-pill mt-2"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Submitting...' : 'Submit Now'}
                                <span>
                                    <i className="fa-solid fa-arrow-right arry1" />
                                </span>
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </>
    );
};

export default BlogCommentForm;
